import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import ImageUpload from '@/components/ImageUpload';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Settings, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  MousePointer,
  Loader2,
  Image,
  ExternalLink
} from 'lucide-react';

function AdminAds() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    link_url: '',
    placement: 'both',
    is_active: true,
    priority: 0
  });

  useEffect(() => {
    if (!user || !user.is_admin) {
      navigate('/');
      return;
    }
    fetchAds();
  }, [user, navigate]);

  async function fetchAds() {
    try {
      const res = await api.get('/admin/ads');
      setAds(res.data);
    } catch (err) {
      toast.error('Erro ao carregar anúncios');
    } finally {
      setLoading(false);
    }
  }

  function openCreateDialog() {
    setEditingAd(null);
    setFormData({
      title: '',
      description: '',
      image_url: '',
      link_url: '',
      placement: 'both',
      is_active: true,
      priority: 0
    });
    setDialogOpen(true);
  }

  function openEditDialog(ad) {
    setEditingAd(ad);
    setFormData({
      title: ad.title,
      description: ad.description || '',
      image_url: ad.image_url,
      link_url: ad.link_url,
      placement: ad.placement,
      is_active: ad.is_active,
      priority: ad.priority
    });
    setDialogOpen(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!formData.title || !formData.image_url || !formData.link_url) {
      toast.error('Preenche todos os campos obrigatórios');
      return;
    }

    setSaving(true);
    try {
      if (editingAd) {
        await api.put('/admin/ads/' + editingAd.id, formData);
        toast.success('Anúncio atualizado');
      } else {
        await api.post('/admin/ads', formData);
        toast.success('Anúncio criado');
      }
      setDialogOpen(false);
      fetchAds();
    } catch (err) {
      toast.error('Erro ao guardar anúncio');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(adId) {
    if (!confirm('Tem certeza que deseja eliminar este anúncio?')) return;
    
    try {
      await api.delete('/admin/ads/' + adId);
      toast.success('Anúncio eliminado');
      fetchAds();
    } catch (err) {
      toast.error('Erro ao eliminar anúncio');
    }
  }

  async function handleToggleActive(ad) {
    try {
      await api.put('/admin/ads/' + ad.id, { is_active: !ad.is_active });
      toast.success(ad.is_active ? 'Anúncio desativado' : 'Anúncio ativado');
      fetchAds();
    } catch (err) {
      toast.error('Erro ao atualizar anúncio');
    }
  }

  async function seedSampleAds() {
    try {
      await api.post('/admin/ads/seed');
      toast.success('Anúncios de exemplo criados');
      fetchAds();
    } catch (err) {
      toast.error('Erro ao criar anúncios de exemplo');
    }
  }

  if (!user || !user.is_admin) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-ruby" />
      </div>
    );
  }

  const placementLabels = {
    homepage: 'Página Inicial',
    story: 'Página do Conto',
    both: 'Ambas'
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-ruby/10 flex items-center justify-center">
                <Settings className="w-6 h-6 text-ruby" />
              </div>
              <div>
                <h1 className="font-brand text-2xl font-bold text-foreground">Gerir Anúncios</h1>
                <p className="text-muted-foreground">Administre os anúncios da plataforma</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={seedSampleAds} className="border-border">
                Criar Exemplos
              </Button>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={openCreateDialog} className="bg-ruby hover:bg-ruby-light">
                    <Plus className="w-4 h-4 mr-2" />
                    Novo Anúncio
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-surface border-border max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="text-foreground">
                      {editingAd ? 'Editar Anúncio' : 'Criar Anúncio'}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Título *</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="Título do anúncio"
                        className="bg-surface-light border-border"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description">Descrição</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Descrição breve (opcional)"
                        className="bg-surface-light border-border"
                        rows={2}
                      />
                    </div>
                    <div className="space-y-2">
                      <ImageUpload
                        value={formData.image_url}
                        onChange={(url) => setFormData({ ...formData, image_url: url })}
                        label="Imagem do Anúncio *"
                        aspectRatio="aspect-[3/1]"
                        recommendedSize="1200×400 · 3:1 · até 150 KB"
                        hint="Banner horizontal largo. Mantém texto importante no terço central."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="link_url">URL do Link *</Label>
                      <Input
                        id="link_url"
                        value={formData.link_url}
                        onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                        placeholder="/premium ou https://..."
                        className="bg-surface-light border-border"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Posicionamento</Label>
                        <Select
                          value={formData.placement}
                          onValueChange={(value) => setFormData({ ...formData, placement: value })}
                        >
                          <SelectTrigger className="bg-surface-light border-border">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-surface border-border">
                            <SelectItem value="homepage">Página Inicial</SelectItem>
                            <SelectItem value="story">Página do Conto</SelectItem>
                            <SelectItem value="both">Ambas</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="priority">Prioridade</Label>
                        <Input
                          id="priority"
                          type="number"
                          value={formData.priority}
                          onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                          className="bg-surface-light border-border"
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-surface-light rounded-lg">
                      <Label htmlFor="is_active" className="cursor-pointer">Ativo</Label>
                      <Switch
                        id="is_active"
                        checked={formData.is_active}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                      />
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 border-border">
                        Cancelar
                      </Button>
                      <Button type="submit" disabled={saving} className="flex-1 bg-ruby hover:bg-ruby-light">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingAd ? 'Atualizar' : 'Criar')}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-surface rounded-xl border border-border p-4">
              <p className="text-muted-foreground text-sm">Total de Anúncios</p>
              <p className="text-2xl font-bold text-foreground">{ads.length}</p>
            </div>
            <div className="bg-surface rounded-xl border border-border p-4">
              <p className="text-muted-foreground text-sm">Ativos</p>
              <p className="text-2xl font-bold text-green-500">
                {ads.filter(function(a) { return a.is_active; }).length}
              </p>
            </div>
            <div className="bg-surface rounded-xl border border-border p-4">
              <p className="text-muted-foreground text-sm">Total de Cliques</p>
              <p className="text-2xl font-bold text-foreground">
                {ads.reduce(function(sum, a) { return sum + a.clicks; }, 0)}
              </p>
            </div>
          </div>

          {/* Ads List */}
          {ads.length > 0 ? (
            <div className="space-y-4">
              {ads.map(function(ad) {
                return (
                  <div 
                    key={ad.id}
                    className={'bg-surface rounded-xl border p-4 flex gap-4 ' + (ad.is_active ? 'border-border' : 'border-red-500/30 opacity-60')}
                  >
                    <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                      <img src={ad.image_url} alt={ad.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-semibold text-foreground">{ad.title}</h3>
                          {ad.description && (
                            <p className="text-sm text-muted-foreground line-clamp-1">{ad.description}</p>
                          )}
                        </div>
                        <span className={'text-xs px-2 py-1 rounded-full ' + (ad.is_active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500')}>
                          {ad.is_active ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Image className="w-3 h-3" />
                          {placementLabels[ad.placement]}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {ad.impressions} impressões
                        </span>
                        <span className="flex items-center gap-1">
                          <MousePointer className="w-3 h-3" />
                          {ad.clicks} cliques
                        </span>
                        <span className="flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" />
                          {ad.link_url}
                        </span>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button variant="outline" size="sm" onClick={() => openEditDialog(ad)} className="border-border">
                          <Edit className="w-4 h-4 mr-1" />Editar
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleToggleActive(ad)} className="border-border">
                          {ad.is_active ? 'Desativar' : 'Ativar'}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDelete(ad.id)} className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-20">
              <Image className="w-16 h-16 text-muted mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">Nenhum anúncio cadastrado</h2>
              <p className="text-muted-foreground mb-6">Cria seu primeiro anúncio ou usa os exemplos</p>
              <div className="flex justify-center gap-4">
                <Button variant="outline" onClick={seedSampleAds} className="border-border">
                  Criar Exemplos
                </Button>
                <Button onClick={openCreateDialog} className="bg-ruby hover:bg-ruby-light">
                  <Plus className="w-4 h-4 mr-2" />Criar Anúncio
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default AdminAds;
